/**
 * Push Notification Service
 * Handles push notification registration and management for iOS/Android
 */
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

/**
 * Apple's (or Android's) current notification permission for Fitxess:
 * 'granted' | 'denied' | 'prompt' (not asked yet), or 'unsupported' off-device.
 * Always read fresh: the user can change it in iPhone Settings at any time.
 */
export async function getPermissionState() {
  if (!Capacitor.isNativePlatform()) return 'unsupported';
  try {
    const status = await PushNotifications.checkPermissions();
    return normalizePermission(status.receive);
  } catch (error) {
    console.error('[Push] Permission check failed:', error);
    return 'prompt';
  }
}

function normalizePermission(receive) {
  if (receive === 'granted' || receive === 'denied') return receive;
  return 'prompt'; // 'prompt' / 'prompt-with-rationale'
}

/**
 * Initialize push notifications: checks the CURRENT permission every time,
 * shows the system permission prompt only when it has never been answered,
 * and registers for push once granted.
 */
export async function initializePushNotifications() {
  if (!Capacitor.isNativePlatform()) {
    console.log('[Push] Skipping - not native platform');
    return { granted: false, permission: 'unsupported', token: null };
  }

  try {
    // Check current permission status
    let permission = await getPermissionState();
    console.log('[Push] Current permission status:', permission);

    // Request permission only if it has never been answered
    if (permission === 'prompt') {
      const requested = await PushNotifications.requestPermissions();
      permission = normalizePermission(requested.receive);
    }

    if (permission !== 'granted') {
      console.log('[Push] Permission not granted');
      return { granted: false, permission, token: null };
    }

    // Register for push notifications (repeat calls are harmless)
    await PushNotifications.register();
    console.log('[Push] Registered for push notifications');

    return { granted: true, permission, token: null };
  } catch (error) {
    console.error('[Push] Initialization failed:', error);
    return { granted: false, permission: await getPermissionState(), token: null, error };
  }
}

/**
 * Add listener for push notification registration
 * Returns the device token for server-side storage
 */
export function addRegistrationListener(callback) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const listener = PushNotifications.addListener('registration', (token) => {
    console.log('[Push] Registration token:', token.value);
    callback(token.value);
  });

  return () => listener.remove();
}

/**
 * Add listener for registration errors
 */
export function addRegistrationErrorListener(callback) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const listener = PushNotifications.addListener('registrationError', (error) => {
    console.error('[Push] Registration error:', error);
    callback(error);
  });

  return () => listener.remove();
}

/**
 * Add listener for received notifications (foreground)
 */
export function addNotificationReceivedListener(callback) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const listener = PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[Push] Notification received:', notification);
    callback(notification);
  });

  return () => listener.remove();
}

/**
 * Add listener for notification taps (opens app)
 */
export function addNotificationActionListener(callback) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const listener = PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[Push] Notification action:', action);
    callback(action);
  });

  return () => listener.remove();
}

/**
 * Get delivered notifications
 */
export async function getDeliveredNotifications() {
  if (!Capacitor.isNativePlatform()) return { notifications: [] };

  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result;
  } catch (error) {
    console.error('[Push] Failed to get delivered notifications:', error);
    return { notifications: [] };
  }
}

/**
 * Remove all delivered notifications
 */
export async function removeAllDeliveredNotifications() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await PushNotifications.removeAllDeliveredNotifications();
    console.log('[Push] All notifications removed');
  } catch (error) {
    console.error('[Push] Failed to remove notifications:', error);
  }
}

export default {
  getPermissionState,
  initializePushNotifications,
  addRegistrationListener,
  addRegistrationErrorListener,
  addNotificationReceivedListener,
  addNotificationActionListener,
  getDeliveredNotifications,
  removeAllDeliveredNotifications,
};
